import React, { Component } from "react";

class GoogleRecaptcha extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isVerifying: false,
      isVerified: false,
      error: null,
    };
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.loadRecaptchaScript();
    this.injectSpinnerAnimation();
  }

  injectSpinnerAnimation = () => {
    if (!document.getElementById("recaptcha-spinner-style")) {
      const style = document.createElement("style");
      style.id = "recaptcha-spinner-style";
      style.textContent = `
        @keyframes recaptcha-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes recaptcha-logo-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  loadRecaptchaScript = () => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    if (window.grecaptcha && window.grecaptcha.execute) {
      return;
    }

    const existingScript = document.querySelector(`script[src*="recaptcha"][src*="${siteKey}"]`);
    if (existingScript) {
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log("reCAPTCHA v3 script loaded");
    };
    script.onerror = () => {
      console.error("Failed to load reCAPTCHA v3 script");
      this.setState({
        error: "Failed to load security verification. Please refresh the page.",
      });
      if (this.props.onError) {
        this.props.onError(new Error("Failed to load reCAPTCHA script"));
      }
    };
    document.body.appendChild(script);
  };

  executeRecaptchaV3 = () => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    if (!window.grecaptcha || !window.grecaptcha.execute) {
      setTimeout(() => {
        if (window.grecaptcha && window.grecaptcha.execute) {
          this.executeRecaptchaV3();
        } else {
          const error = new Error("Security verification service is not ready. Please refresh the page.");
          this.setState({
            error: error.message,
            isVerifying: false,
          });
          if (this.props.onError) {
            this.props.onError(error);
          }
        }
      }, 500);
      return;
    }

    this.setState({
      isVerifying: true,
      error: null,
      isVerified: false
    });

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(siteKey, { action: "submit_payment" })
        .then((token) => {
          console.log("reCAPTCHA v3 token received");

          this.setState({
            isVerified: true,
            isVerifying: false,
            error: null,
          });
          if (this.props.onVerify) {
            this.props.onVerify(token);
          }
        })
        .catch((error) => {
          console.error("reCAPTCHA v3 execution error:", error);
          this.setState({
            isVerifying: false,
            error: "Security verification failed. Please try again.",
          });
          if (this.props.onError) {
            this.props.onError(error);
          }
        });
    });
  };

  componentWillUnmount() {
  }

  render() {
    const { isVerifying, isVerified, error } = this.state;

    const checkboxClasses = [
      "d-inline-flex",
      "align-items-center",
      "p-2",
      "border",
      "rounded",
      "user-select-none",
      "position-relative",
    ];

    let iconBorderClass = "";
    let iconBgClass = "bg-white";
    if (isVerified) {
      iconBorderClass = "";
      iconBgClass = "bg-success";
    } else if (isVerifying) {
      iconBorderClass = "border-primary";
    }

    return (
      <div className="w-100">
        <div ref={this.containerRef} className="d-flex justify-content-center mb-3">
          <div className="d-inline-block">
            <div
              className={checkboxClasses.join(" ")}
              style={{
                minWidth: "304px",
                minHeight: "78px",
              }}
            >
              <div className="d-flex align-items-center w-100" style={{ position: "relative" }}>
                <div
                  className={`d-flex align-items-center justify-content-center ${iconBgClass} ${iconBorderClass}`}
                  style={{
                    width: "24px",
                    height: "24px",
                    border: isVerified ? "none" : "2px solid",
                    borderColor: isVerified ? "transparent" : isVerifying ? "#4285f4" : "#9e9e9e",
                    borderRadius: "2px",
                    flexShrink: 0,
                    cursor: isVerified ? "default" : isVerifying ? "wait" : "pointer",
                    transition: "background-color 0.2s, border-color 0.2s",
                    boxSizing: "border-box",
                    marginRight: "12px",
                  }}
                  onClick={!isVerified && !isVerifying ? this.executeRecaptchaV3 : undefined}
                  onMouseEnter={(e) => {
                    if (!isVerifying && !isVerified) {
                      e.currentTarget.style.backgroundColor = "#f5f5f5";
                      e.currentTarget.style.borderColor = "#4285f4";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isVerifying && !isVerified) {
                      e.currentTarget.style.backgroundColor = "#fff";
                      e.currentTarget.style.borderColor = "#9e9e9e";
                    }
                  }}
                  role="button"
                  tabIndex={!isVerified && !isVerifying ? 0 : -1}
                  onKeyPress={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !isVerified && !isVerifying) {
                      e.preventDefault();
                      this.executeRecaptchaV3();
                    }
                  }}
                >
                  {isVerified ? (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{
                        stroke: "#fff",
                        strokeWidth: "3",
                        strokeLinecap: "round",
                        strokeLinejoin: "round",
                      }}
                    >
                      <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.5" fill="none" />
                    </svg>
                  ) : isVerifying ? (
                    <div
                      className="border border-primary"
                      style={{
                        width: "14px",
                        height: "14px",
                        borderTopColor: "transparent",
                        borderRightColor: "transparent",
                        borderRadius: "50%",
                        animation: "recaptcha-spin 0.8s linear infinite",
                      }}
                    ></div>
                  ) : null}
                </div>

                <span
                  className="text-center"
                  style={{
                    color: "#202124",
                    fontSize: "18px",
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    whiteSpace: "nowrap",
                    pointerEvents: "none"
                  }}
                >
                  I'm not a robot
                </span>

                <div className="d-flex flex-column align-items-end" style={{ marginLeft: "auto", flexShrink: 0 }}>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle mx-auto"
                    style={{
                      width: "32px",
                      height: "32px",
                      background: "linear-gradient(135deg, #4285f4 0%, #34a853 50%, #fbbc04 75%, #ea4335 100%)",
                      flexShrink: 0,
                      animation: "recaptcha-logo-spin 3s linear infinite",
                    }}
                  >
                    <div
                      className="bg-white rounded-circle"
                      style={{
                        width: "24px",
                        height: "24px",
                      }}
                    ></div>
                  </div>
                  <div className="mt-1 text-right" style={{ fontSize: "11px", color: "#70757a" }}>
                    reCAPTCHA
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-2 text-center" style={{ fontSize: "11px", color: "#70757a" }}>
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary text-decoration-none"
                style={{ marginRight: "4px" }}
              >
                Privacy
              </a>
              <span style={{ margin: "0 4px" }}> - </span>
              <a
                href="https://policies.google.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary text-decoration-none"
                style={{ marginLeft: "4px" }}
              >
                Terms
              </a>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mt-3" role="alert">
            {error}
          </div>
        )}
      </div>
    );
  }
}

export default GoogleRecaptcha;

